/**
 * Veryplants Wholesale lead capture form.
 * Multi-step modal. Each step posts to the Apps Script Web App so partial
 * leads are captured even if the user bails before completing.
 *
 * Region: window.VP_REGION ('us' or 'ca'), set by lead-capture-config.js
 * Endpoint: window.VP_LEAD_CAPTURE_URL
 */
(function () {
  "use strict";

  const ENDPOINT = window.VP_LEAD_CAPTURE_URL || "";
  const REGION = window.VP_REGION || "us";
  const CONTACT = (window.VP_REGION_CONTACT && window.VP_REGION_CONTACT[REGION]) || {};
  const DEMO_MODE = !ENDPOINT || ENDPOINT.indexOf("PASTE_") === 0;

  const STORAGE_KEY = "vp_lead_capture_state_" + REGION;
  const GEO_CACHE_KEY = "vp_geo_country";
  const GEO_BANNER_DISMISSED_KEY = "vp_geo_banner_dismissed";

  // Anti-spam. The webhook is anonymous-public, so a bot crawler could fire
  // POSTs at it directly, but anyone driving the form via a real DOM (a real
  // browser OR a headless one) will fail at least one of these two checks.
  // - HONEYPOT_NAME is a hidden input bots tend to fill via name-matching.
  // - MIN_FILL_MS rejects submissions less than 2s after the modal opened.
  // The smoke test sets window.VP_SKIP_BOT_CHECK so it can drive the form fast.
  const HONEYPOT_NAME = "company_website_optional";
  const MIN_FILL_MS = 2000;
  let formOpenedAt = 0;

  // Best-effort visitor geolocation. Lookup result lives here so we can include
  // it in payloads (analysis-only, never used to override REGION) and so we can
  // surface a "looks like you're in X" banner if the visitor is on the wrong
  // regional domain.
  let detectedCountry = null;
  try { detectedCountry = sessionStorage.getItem(GEO_CACHE_KEY) || null; } catch (e) { /* ignore */ }

  // ---- State ----
  const state = {
    flow: null, // 'existing_customer_view' | 'info_request' | 'full_application'
    stage: null, // 'step_1' | 'step_2' | 'step_3' | 'step_4_complete'
    data: {},
    sending: false,
  };

  // Restore in-progress form from localStorage if any. State older than 24h
  // is discarded so a different visitor on a shared machine never sees the
  // previous applicant's data.
  const STATE_TTL_MS = 24 * 60 * 60 * 1000;
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    const fresh = saved && saved.savedAt && (Date.now() - saved.savedAt) < STATE_TTL_MS;
    if (fresh && saved.data && saved.data.email) {
      Object.assign(state, saved);
    } else if (saved) {
      // Stale; clear so the next save doesn't leave an aged record.
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    }
  } catch (e) { /* ignore */ }

  function saveLocal() {
    try {
      const snapshot = Object.assign({}, state, { savedAt: Date.now() });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (e) { /* ignore */ }
  }
  function clearLocal() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  // ---- API ----
  async function postLead(payload) {
    if (DEMO_MODE) {
      console.info("[VP demo] would POST lead:", payload);
      return { ok: true, demo: true };
    }
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        // Apps Script Web Apps don't accept preflighted CORS, so use text/plain.
        // Apps Script reads e.postData.contents as the raw body regardless.
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      return json;
    } catch (err) {
      console.warn("postLead failed:", err);
      return { ok: false, error: String(err) };
    }
  }

  // ---- Open / close ----
  function openModal(initialFlow) {
    document.body.style.overflow = "hidden";
    overlay.classList.add("is-open");
    formOpenedAt = Date.now();
    // Clear any value a previous bot may have left in the honeypot.
    const hp = overlay.querySelector('input[name="' + HONEYPOT_NAME + '"]');
    if (hp) hp.value = "";
    // initialFlow === "prospect_choice" lets a CTA skip the are-you-an-existing-
    // customer prompt and jump straight to the application path. "contact"
    // opens directly on the sales-team contact card (email + phone + text)
    // for the "Email our sales team" CTA. Defaults to the customer_question
    // filter for cold opens.
    if (initialFlow === "prospect_choice") {
      state.flow = null;
      goToStep("prospect_choice");
    } else if (initialFlow === "contact") {
      state.flow = "contact_only";
      goToStep("contact_card");
    } else {
      goToStep("customer_question");
    }
  }
  function closeModal() {
    document.body.style.overflow = "";
    overlay.classList.remove("is-open");
  }

  // ---- Routing ----
  // Logical "screens": customer_question, existing_contact, prospect_choice,
  // info_step, full_step1, full_step2, full_step3, full_step4, completed
  let currentScreen = null;
  function goToStep(screen) {
    currentScreen = screen;
    render();
  }

  // ---- Render ----
  function render() {
    body.innerHTML = "";
    progress.innerHTML = "";
    footer.innerHTML = "";
    titleEl.textContent = "Become a Veryplants wholesaler";

    if (currentScreen === "customer_question") {
      titleEl.textContent = "Are you already a Veryplants customer?";
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 1 of 2</div>
          <h2 class="vp-form__heading">First, a quick question.</h2>
          <p class="vp-form__lede">It helps us route you to the right person and avoid asking for information you've already shared.</p>
          <div class="vp-form__choices">
            <button type="button" class="vp-form__choice" data-choice="existing">
              <strong>Yes, I already buy Veryplants products</strong>
              <span>We'll show you the sales team's direct contact details.</span>
            </button>
            <button type="button" class="vp-form__choice" data-choice="new">
              <strong>No, I'm new to Veryplants</strong>
              <span>We'll walk you through a quick application or info request.</span>
            </button>
          </div>
        </div>`;
      renderProgress(0, 0);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="close">Cancel</button>
      `;
      body.querySelectorAll(".vp-form__choice").forEach((b) => b.addEventListener("click", (e) => {
        const c = e.currentTarget.dataset.choice;
        if (c === "existing") {
          state.flow = "existing_customer_view";
          // Don't write an empty row to the sheet for "existing customer" path:
          // they're not a lead, they're a known account.
          goToStep("existing_contact");
        } else {
          goToStep("prospect_choice");
        }
      }));
    }

    else if (currentScreen === "existing_contact") {
      titleEl.textContent = "We'd love to hear from you.";
      const textNumberHref = "sms:" + (CONTACT.textSales || "").replace(/[^+\d]/g, "");
      const phoneHref = "tel:" + CONTACT.tollFree.replace(/[^+\d]/g, "");
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Existing customer</div>
          <h2 class="vp-form__heading">Reach our sales team directly.</h2>
          <p class="vp-form__lede">Whether it's a reorder, a question, or you just want to talk through a new product line, our sales team handles the wholesale relationship and is the fastest way to get an answer.</p>
          <div class="vp-form__contact-card">
            <div class="vp-form__contact-row">
              <span class="label">Email</span>
              <a href="mailto:${CONTACT.email}">${CONTACT.email}</a>
            </div>
            <div class="vp-form__contact-row">
              <span class="label">Toll-free</span>
              <a href="${phoneHref}">${CONTACT.tollFree}</a>
            </div>
            <div class="vp-form__contact-row">
              <span class="label">${CONTACT.textSalesLabel || "Text sales"}</span>
              <a href="${textNumberHref}">${CONTACT.textSales}</a>
            </div>
          </div>
        </div>`;
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="close">Got it</button>
      `;
    }

    else if (currentScreen === "contact_card") {
      // Cold-visitor contact card. Opened from the "Email our sales team" CTA
      // so people can see the email AND the phone numbers, instead of being
      // dumped straight into their mail client. Same UI as existing_contact,
      // different framing in the eyebrow and lede.
      titleEl.textContent = "Talk to our sales team.";
      const textNumberHref = "sms:" + (CONTACT.textSales || "").replace(/[^+\d]/g, "");
      const phoneHref = "tel:" + CONTACT.tollFree.replace(/[^+\d]/g, "");
      const mailtoHref = `mailto:${CONTACT.email}?subject=Wholesale%20enquiry`;
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Contact us</div>
          <h2 class="vp-form__heading">Email or call us directly.</h2>
          <p class="vp-form__lede">For trade enquiries, reorders, or anything wholesale-related, our sales team is the fastest path. We'll respond within one business day.</p>
          <div class="vp-form__contact-card">
            <div class="vp-form__contact-row">
              <span class="label">Email</span>
              <a href="${mailtoHref}">${CONTACT.email}</a>
            </div>
            <div class="vp-form__contact-row">
              <span class="label">Toll-free</span>
              <a href="${phoneHref}">${CONTACT.tollFree}</a>
            </div>
            <div class="vp-form__contact-row">
              <span class="label">${CONTACT.textSalesLabel || "Text sales"}</span>
              <a href="${textNumberHref}">${CONTACT.textSales}</a>
            </div>
          </div>
        </div>`;
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="close">Close</button>
      `;
    }

    else if (currentScreen === "prospect_choice") {
      titleEl.textContent = "How would you like to start?";
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 2 of 2</div>
          <h2 class="vp-form__heading">Two paths in.</h2>
          <p class="vp-form__lede">Both lead to a conversation with our sales team. The full application captures the information they'd ask anyway, so the call is shorter.</p>
          <div class="vp-form__choices">
            <button type="button" class="vp-form__choice" data-choice="info">
              <strong>Just looking, tell me more</strong>
              <span>Quick form (about 30 seconds). We'll send you the wholesale catalog and have our sales team reach out.</span>
            </button>
            <button type="button" class="vp-form__choice" data-choice="full">
              <strong>I'm ready to apply</strong>
              <span>Full application (about 5 minutes). Includes business details and tax/resale documentation.</span>
            </button>
          </div>
        </div>`;
      renderProgress(0, 0);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
      `;
      body.querySelectorAll(".vp-form__choice").forEach((b) => b.addEventListener("click", (e) => {
        const c = e.currentTarget.dataset.choice;
        if (c === "info") {
          state.flow = "info_request";
          goToStep("info_step");
        } else {
          state.flow = "full_application";
          goToStep("full_step1");
        }
      }));
    }

    else if (currentScreen === "info_step") {
      titleEl.textContent = "Tell us about you and your shop.";
      const d = state.data;
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Information request</div>
          <h2 class="vp-form__heading">A few quick details.</h2>
          <div class="vp-form__field-row">
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">Your name</span>
              <input class="vp-form__input" name="name_first" placeholder="First name" value="${esc(d.name_first || "")}" required>
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label">&nbsp;</span>
              <input class="vp-form__input" name="name_last" placeholder="Last name" value="${esc(d.name_last || "")}">
            </label>
          </div>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Email</span>
            <input class="vp-form__input" name="email" type="email" placeholder="you@yourshop.com" value="${esc(d.email || "")}" required>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Business name</span>
            <input class="vp-form__input" name="business_name" placeholder="Your shop's name" value="${esc(d.business_name || "")}" required>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Business type</span>
            <select class="vp-form__select" name="business_type" required>
              ${businessTypeOptions(d.business_type)}
            </select>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label">What would you like to know?</span>
            <textarea class="vp-form__textarea" name="notes" placeholder="Optional: questions, products of interest, anything else.">${esc(d.notes || "")}</textarea>
          </label>
          ${consentMarkup(d.consent_given)}
          <div class="vp-form__error" data-error hidden></div>
        </div>`;
      renderProgress(1, 1);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="submit-info">Send</button>
      `;
    }

    else if (currentScreen === "full_step1") {
      titleEl.textContent = "Your contact details.";
      const d = state.data;
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 1 of 4: You</div>
          <h2 class="vp-form__heading">Let's start with the basics.</h2>
          <div class="vp-form__field-row">
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">First name</span>
              <input class="vp-form__input" name="name_first" value="${esc(d.name_first || "")}" required>
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">Last name</span>
              <input class="vp-form__input" name="name_last" value="${esc(d.name_last || "")}" required>
            </label>
          </div>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Email</span>
            <input class="vp-form__input" name="email" type="email" value="${esc(d.email || "")}" required>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Phone</span>
            <input class="vp-form__input" name="phone" type="tel" value="${esc(d.phone || "")}" required>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label">Your role at the company</span>
            <input class="vp-form__input" name="role_at_company" placeholder="Owner, buyer, etc." value="${esc(d.role_at_company || "")}">
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Business name</span>
            <input class="vp-form__input" name="business_name" value="${esc(d.business_name || "")}" required>
          </label>
          ${consentMarkup(d.consent_given)}
          <div class="vp-form__error" data-error hidden></div>
        </div>`;
      renderProgress(4, 0);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="submit-full-1">Continue →</button>
      `;
    }

    else if (currentScreen === "full_step2") {
      titleEl.textContent = "Your business.";
      const d = state.data;
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 2 of 4: Your business</div>
          <h2 class="vp-form__heading">Tell us about your shop.</h2>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Business type</span>
            <select class="vp-form__select" name="business_type" required>${businessTypeOptions(d.business_type)}</select>
          </label>
          <div class="vp-form__field-row">
            <label class="vp-form__field">
              <span class="vp-form__label">Years in business</span>
              <input class="vp-form__input" name="years_in_business" type="number" min="0" value="${esc(d.years_in_business || "")}">
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">Number of locations</span>
              <select class="vp-form__select" name="number_of_locations" required>${locationsOptions(d.number_of_locations)}</select>
            </label>
          </div>
          <label class="vp-form__field">
            <span class="vp-form__label">Website or social handle</span>
            <input class="vp-form__input" name="website" placeholder="yourshop.com or @instagram" value="${esc(d.website || "")}">
          </label>
          <div class="vp-form__field-row">
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">City</span>
              <input class="vp-form__input" name="address_city" value="${esc(d.address_city || "")}" required>
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">${REGION === "ca" ? "Province" : "State"}</span>
              <input class="vp-form__input" name="address_state_province" value="${esc(d.address_state_province || "")}" required>
            </label>
          </div>
          <input type="hidden" name="address_country" value="${REGION === "ca" ? "Canada" : "United States"}">
          <div class="vp-form__error" data-error hidden></div>
        </div>`;
      renderProgress(4, 1);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="submit-full-2">Continue →</button>
      `;
    }

    else if (currentScreen === "full_step3") {
      titleEl.textContent = "Sales potential.";
      const d = state.data;
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 3 of 4: Sales potential</div>
          <h2 class="vp-form__heading">Help us size the relationship.</h2>
          <label class="vp-form__field">
            <span class="vp-form__label vp-form__label-required">Estimated monthly volume</span>
            <select class="vp-form__select" name="estimated_monthly_volume" required>${volumeOptions(d.estimated_monthly_volume)}</select>
            <span class="vp-form__hint">Across all sizes (3qt, 5qt, 10qt). A rough estimate is fine; we'll refine on the call.</span>
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label">Other potting mix brands you currently carry</span>
            <input class="vp-form__input" name="other_brands_carried" placeholder="e.g. Espoma, Fox Farm, Black Gold" value="${esc(d.other_brands_carried || "")}">
          </label>
          <label class="vp-form__field">
            <span class="vp-form__label">How did you hear about Veryplants?</span>
            <select class="vp-form__select" name="how_heard">${howHeardOptions(d.how_heard)}</select>
          </label>
          <div class="vp-form__error" data-error hidden></div>
        </div>`;
      renderProgress(4, 2);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="submit-full-3">Continue →</button>
      `;
    }

    else if (currentScreen === "full_step4") {
      titleEl.textContent = "Tax and documentation.";
      const d = state.data;
      const fileLabel = d.us_resale_cert_file
        ? `<span class="vp-form__file-name">${esc(d.us_resale_cert_file.filename || "selected")}</span>`
        : `<span class="vp-form__file-label">Choose a PDF or image (max 10MB)</span>`;

      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Step 4 of 4: Tax and docs</div>
          <h2 class="vp-form__heading">Almost done.</h2>
          ${REGION === "us" ? `
            <p class="vp-form__lede">For US wholesale customers, we collect a Resale Certificate so you're not charged sales tax on wholesale orders. PDF or image both work.</p>
            <div class="vp-form__field-row">
              <label class="vp-form__field">
                <span class="vp-form__label vp-form__label-required">Cert / registration number</span>
                <input class="vp-form__input" name="us_resale_cert_number" placeholder="As shown on the certificate" value="${esc(d.us_resale_cert_number || "")}" required>
                <span class="vp-form__hint">The ID number on the cert itself. Ask our sales team if unsure.</span>
              </label>
              <label class="vp-form__field">
                <span class="vp-form__label vp-form__label-required">Expiration date</span>
                <input class="vp-form__input" name="us_resale_cert_expiration" type="text" placeholder="YYYY-MM-DD or NoExpiry" value="${esc(d.us_resale_cert_expiration || "")}" required>
                <span class="vp-form__hint">Type "NoExpiry" if the cert doesn't expire.</span>
              </label>
            </div>
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">Upload Resale Certificate</span>
              <label class="vp-form__file">
                <input type="file" name="us_resale_cert_file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                ${fileLabel}
              </label>
              <span class="vp-form__hint">PDF or image, max 10MB. If you don't have one handy, you can email it to <a href="mailto:sales@veryplants.com">sales@veryplants.com</a> after submission.</span>
            </label>
          ` : `
            <p class="vp-form__lede">For Canadian wholesale customers, we capture your CRA Business Number and GST/HST registration. Provincial taxes are handled later if applicable.</p>
            <label class="vp-form__field">
              <span class="vp-form__label vp-form__label-required">GST/HST number</span>
              <input class="vp-form__input" name="ca_gst_hst_number" placeholder="123456789RT0001" value="${esc(d.ca_gst_hst_number || "")}" required>
              <span class="vp-form__hint">Format: 9-digit BN + RT program code (e.g. 123456789RT0001)</span>
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label">CRA Business Number (if different)</span>
              <input class="vp-form__input" name="ca_business_number" value="${esc(d.ca_business_number || "")}">
            </label>
            <label class="vp-form__field">
              <span class="vp-form__label">PST / QST registration (if applicable)</span>
              <input class="vp-form__input" name="ca_provincial_tax_notes" placeholder="e.g. BC PST 1234-5678 or Quebec QST" value="${esc(d.ca_provincial_tax_notes || "")}">
            </label>
          `}
          <label class="vp-form__field">
            <span class="vp-form__label">Anything else you'd like our sales team to know?</span>
            <textarea class="vp-form__textarea" name="notes">${esc(d.notes || "")}</textarea>
          </label>
          <div class="vp-form__error" data-error hidden></div>
        </div>`;
      renderProgress(4, 3);
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--ghost" data-action="back">← Back</button>
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="submit-full-4">Submit application</button>
      `;

      const fileInput = body.querySelector('input[name="us_resale_cert_file"]');
      if (fileInput) {
        fileInput.addEventListener("change", async (e) => {
          const f = e.target.files[0];
          if (!f) return;
          if (f.size > 10 * 1024 * 1024) { setError("File too large. Max 10MB."); return; }
          // Snapshot whatever the user has already typed into the other fields
          // BEFORE we re-render, otherwise the re-render reads from a stale
          // state.data and visibly clears their cert number / expiration.
          captureFromForm();
          const b64 = await fileToBase64(f);
          state.data.us_resale_cert_file = { filename: f.name, mime_type: f.type, base64: b64 };
          render(); // refresh to show filename
        });
      }
    }

    else if (currentScreen === "completed") {
      titleEl.textContent = "Thanks, we've got it.";
      body.innerHTML = `
        <div class="vp-form__step is-active">
          <div class="vp-form__eyebrow">Submitted</div>
          <h2 class="vp-form__heading">We'll be in touch within one business day.</h2>
          <p class="vp-form__lede">${state.flow === "full_application"
            ? `Our sales team has been notified of your application and will reach out soon. If you need to add anything, email <a href="mailto:${CONTACT.email}">${CONTACT.email}</a> with your business name in the subject.`
            : `Robert (our wholesale coordinator) will reach out shortly with the catalog and the answers to your questions. If you'd rather talk now, call <a href="tel:${(CONTACT.tollFree || "").replace(/[^+\d]/g, "")}">${CONTACT.tollFree}</a> and ask for the sales team.`}</p>
          <div class="vp-form__success">✓ Application saved; confirmation also sent by email.</div>
        </div>`;
      footer.innerHTML = `
        <button type="button" class="vp-form__btn vp-form__btn--primary" data-action="close">Close</button>
      `;
    }

    // Wire actions
    footer.querySelectorAll("[data-action]").forEach((b) => b.addEventListener("click", handleAction));
  }

  function renderProgress(total, done) {
    progress.innerHTML = "";
    for (let i = 0; i < total; i++) {
      const el = document.createElement("div");
      el.className = "vp-form__progress-step";
      if (i < done) el.classList.add("is-done");
      else if (i === done) el.classList.add("is-current");
      progress.appendChild(el);
    }
  }

  function consentMarkup(checked) {
    return `
      <label class="vp-form__consent">
        <input type="checkbox" name="consent_given" ${checked ? "checked" : ""}>
        <span>Yes, I'd like Veryplants to contact me about wholesale opportunities. We don't share your information with third parties and you can opt out any time.</span>
      </label>`;
  }
  function businessTypeOptions(v) {
    const opts = ["Garden centre", "Plant shop", "Nursery", "Online retailer", "Florist", "Landscape designer", "Other"];
    return ["<option value=\"\">Select…</option>"].concat(opts.map(o => `<option value="${esc(o)}" ${v === o ? "selected" : ""}>${esc(o)}</option>`)).join("");
  }
  function locationsOptions(v) {
    const opts = ["1 (single location)", "2–5", "6–15", "16+"];
    return ["<option value=\"\">Select…</option>"].concat(opts.map(o => `<option value="${esc(o)}" ${v === o ? "selected" : ""}>${esc(o)}</option>`)).join("");
  }
  function volumeOptions(v) {
    const opts = ["1–5 cases / month", "6–20 cases / month", "21–50 cases / month", "51–100 cases / month", "100+ cases / month", "Not sure yet"];
    return ["<option value=\"\">Select…</option>"].concat(opts.map(o => `<option value="${esc(o)}" ${v === o ? "selected" : ""}>${esc(o)}</option>`)).join("");
  }
  function howHeardOptions(v) {
    const opts = ["Social media", "Trade show", "Recommendation from another retailer", "Direct outreach from Veryplants", "Search engine", "Other"];
    return ["<option value=\"\">Select…</option>"].concat(opts.map(o => `<option value="${esc(o)}" ${v === o ? "selected" : ""}>${esc(o)}</option>`)).join("");
  }

  // ---- Validation + submission ----
  async function handleAction(e) {
    const action = e.currentTarget.dataset.action;
    if (state.sending) return;

    if (action === "close") return closeModal();
    if (action === "back") return handleBack();

    captureFromForm();

    // Final submissions: must succeed before we advance to "completed".
    // Intermediate submissions (between full-app steps) are allowed to fail
    // silently because the user is mid-flow and the final POST is a complete
    // record anyway via the upsert.
    if (action === "submit-info") {
      if (!validate(["name_first", "email", "business_name", "business_type"])) return;
      if (!state.data.consent_given) return setError("Please tick the consent box to continue.");
      if (looksLikeBot()) { clearLocal(); goToStep("completed"); return; }
      state.stage = "step_1";
      const result = await sendStep({ stage: "step_4_complete" }); // info request is one-shot
      if (!result || !result.ok) return setError("Sorry, we couldn't submit just now. Please try again, or email sales@veryplants.com directly.");
      clearLocal();
      goToStep("completed");
    }
    else if (action === "submit-full-1") {
      if (!validate(["name_first", "name_last", "email", "phone", "business_name"])) return;
      if (!state.data.consent_given) return setError("Please tick the consent box to continue.");
      state.stage = "step_1";
      await sendStep({ stage: "step_1" }); // intermediate save, allow silent failure
      goToStep("full_step2");
    }
    else if (action === "submit-full-2") {
      if (!validate(["business_type", "number_of_locations", "address_city", "address_state_province"])) return;
      state.stage = "step_2";
      await sendStep({ stage: "step_2" }); // intermediate save, allow silent failure
      goToStep("full_step3");
    }
    else if (action === "submit-full-3") {
      if (!validate(["estimated_monthly_volume"])) return;
      state.stage = "step_3";
      await sendStep({ stage: "step_3" }); // intermediate save, allow silent failure
      goToStep("full_step4");
    }
    else if (action === "submit-full-4") {
      if (REGION === "ca") {
        if (!validate(["ca_gst_hst_number"])) return;
      } else if (REGION === "us") {
        if (!validate(["us_resale_cert_number", "us_resale_cert_expiration"])) return;
        if (!state.data.us_resale_cert_file) {
          if (!confirm("You haven't uploaded the Resale Certificate file. The number and expiration are saved, but our sales team will need the cert document to approve your account. Email it to " + (CONTACT.email || "the sales team") + " afterward, or upload it now. Submit anyway?")) return;
        }
      }
      if (looksLikeBot()) { clearLocal(); goToStep("completed"); return; }
      state.stage = "step_4_complete";
      const result = await sendStep({ stage: "step_4_complete" });
      if (!result || !result.ok) return setError("Sorry, we couldn't submit your application just now. Please try again in a moment, or email sales@veryplants.com directly with your details.");
      clearLocal();
      goToStep("completed");
    }
  }

  function handleBack() {
    if (currentScreen === "existing_contact") return goToStep("customer_question");
    if (currentScreen === "prospect_choice") return goToStep("customer_question");
    if (currentScreen === "info_step") return goToStep("prospect_choice");
    if (currentScreen === "full_step1") return goToStep("prospect_choice");
    if (currentScreen === "full_step2") return goToStep("full_step1");
    if (currentScreen === "full_step3") return goToStep("full_step2");
    if (currentScreen === "full_step4") return goToStep("full_step3");
  }

  function captureFromForm() {
    body.querySelectorAll("input, select, textarea").forEach((el) => {
      if (el.type === "file") return;
      const name = el.name;
      if (!name) return;
      if (name === HONEYPOT_NAME) return; // never persist honeypot value
      if (el.type === "checkbox") state.data[name] = el.checked;
      else state.data[name] = el.value;
    });
    saveLocal();
  }

  // Returns true if this submission looks like a bot. Caller should silently
  // close the modal and pretend success: we don't want to teach the bot.
  function looksLikeBot() {
    if (window.VP_SKIP_BOT_CHECK) return false;
    const hp = overlay.querySelector('input[name="' + HONEYPOT_NAME + '"]');
    if (hp && hp.value && hp.value.length > 0) return true;
    const elapsed = Date.now() - (formOpenedAt || 0);
    if (formOpenedAt && elapsed < MIN_FILL_MS) return true;
    return false;
  }

  function validate(requiredKeys) {
    for (const k of requiredKeys) {
      const v = state.data[k];
      if (v === undefined || v === null || String(v).trim() === "") {
        setError("Please fill in: " + humanize(k));
        return false;
      }
    }
    if (requiredKeys.includes("email") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.data.email)) {
      setError("Please enter a valid email address.");
      return false;
    }
    setError(null);
    return true;
  }
  function humanize(k) { return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()); }
  function setError(msg) {
    const el = body.querySelector("[data-error]");
    if (!el) return;
    if (!msg) { el.hidden = true; el.textContent = ""; return; }
    el.hidden = false;
    el.textContent = msg;
  }

  async function sendStep(extra) {
    state.sending = true;
    setSpinner(true);
    const payload = Object.assign(
      {},
      state.data,
      {
        region: REGION,
        flow: state.flow,
        stage: extra.stage || state.stage,
        // Best-effort IP-derived country at the time of submit. Null if the
        // lookup hasn't completed or failed. Stored for analysis only; never
        // used to override the REGION the user actually flowed through.
        detected_country: detectedCountry || null,
      }
    );
    // Don't post empty rows for the existing-customer view
    if (state.flow === "existing_customer_view") {
      state.sending = false; setSpinner(false);
      return { ok: true, skipped: true };
    }
    const result = await postLead(payload);
    state.sending = false;
    setSpinner(false);
    if (!result || !result.ok) console.warn("step send failed:", result);
    return result;
  }

  function setSpinner(on) {
    const btn = footer.querySelector(".vp-form__btn--primary");
    if (!btn) return;
    if (on) {
      btn.dataset.label = btn.textContent;
      btn.disabled = true;
      btn.innerHTML = '<span class="vp-form__spinner"></span> Saving…';
    } else if (btn.dataset.label) {
      btn.disabled = false;
      btn.textContent = btn.dataset.label;
    }
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = String(r.result);
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  function esc(s) {
    return String(s || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  // ---- DOM scaffolding ----
  const overlay = document.createElement("div");
  overlay.className = "vp-form-overlay";
  overlay.innerHTML = `
    <div class="vp-form" role="dialog" aria-modal="true" aria-labelledby="vp-form-title">
      <div class="vp-form__header">
        <div class="vp-form__title" id="vp-form-title">Become a Veryplants wholesaler</div>
        <button type="button" class="vp-form__close" data-action="close" aria-label="Close form">×</button>
      </div>
      ${DEMO_MODE ? '<div class="vp-form__demo-banner">Demo mode: submissions go to the browser console only. Configure VP_LEAD_CAPTURE_URL to enable.</div>' : ''}
      <div class="vp-form__progress" id="vp-form-progress"></div>
      <div class="vp-form__body" id="vp-form-body"></div>
      <div class="vp-form__footer" id="vp-form-footer"></div>
      <input type="text" name="${HONEYPOT_NAME}" class="vp-form__hp" tabindex="-1" autocomplete="off" aria-hidden="true">
    </div>`;
  document.body.appendChild(overlay);
  const titleEl = overlay.querySelector("#vp-form-title");
  const body = overlay.querySelector("#vp-form-body");
  const progress = overlay.querySelector("#vp-form-progress");
  const footer = overlay.querySelector("#vp-form-footer");
  overlay.querySelector(".vp-form__close").addEventListener("click", closeModal);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeModal(); });

  // ---- Public API ----
  window.VPLeadCapture = { open: openModal, close: closeModal };

  // ---- Auto-bind any element with data-vp-open-form ----
  document.addEventListener("click", (e) => {
    const t = e.target.closest("[data-vp-open-form]");
    if (!t) return;
    e.preventDefault();
    openModal();
  });

  // ---- Auto-bind any element with data-vp-open-contact (skips the form,
  //      lands on the cold-visitor contact card with email + phone) ----
  document.addEventListener("click", (e) => {
    const t = e.target.closest("[data-vp-open-contact]");
    if (!t) return;
    e.preventDefault();
    openModal("contact");
  });

  // ---- Geo-detect (best-effort, ipapi.co free tier) ----
  // Suggests switching domains when the visitor's IP country mismatches the
  // domain they landed on. NEVER auto-redirects; the user picks. Result is
  // also captured into the lead payload for later analysis.
  function showRegionBanner(detectedRegionLabel, switchUrl) {
    if (!document.body) return;
    if (document.body.querySelector(".vp-region-banner")) return;
    let dismissed = false;
    try { dismissed = !!sessionStorage.getItem(GEO_BANNER_DISMISSED_KEY); } catch (e) {}
    if (dismissed) return;
    const banner = document.createElement("div");
    banner.className = "vp-region-banner";
    banner.setAttribute("role", "status");
    banner.innerHTML = `
      <span class="vp-region-banner__msg">Looks like you're in ${esc(detectedRegionLabel)}. Want the right wholesale page for your region?</span>
      <a class="vp-region-banner__btn vp-region-banner__btn--primary" href="${esc(switchUrl)}">Yes, switch</a>
      <button class="vp-region-banner__btn" type="button" data-action="region-banner-dismiss" aria-label="Dismiss region suggestion">No, stay here</button>
    `;
    document.body.insertBefore(banner, document.body.firstChild);
    banner.querySelector('[data-action="region-banner-dismiss"]').addEventListener("click", () => {
      try { sessionStorage.setItem(GEO_BANNER_DISMISSED_KEY, "1"); } catch (e) {}
      banner.remove();
    });
  }
  function maybeShowRegionBanner() {
    if (!detectedCountry) return;
    if (REGION === "us" && detectedCountry === "CA") {
      showRegionBanner("Canada", "https://wholesale.veryplants.ca/");
    } else if (REGION === "ca" && detectedCountry !== "CA") {
      showRegionBanner("the US", "https://wholesale.veryplants.com/");
    }
  }
  if (detectedCountry) {
    // Already cached this session.
    maybeShowRegionBanner();
  } else {
    // Fire and forget. ipapi.co free tier supports CORS and gives ~30k
    // requests/month per source IP. If it fails, we silently degrade to
    // hostname-only routing.
    try {
      fetch("https://ipapi.co/json/", { credentials: "omit" })
        .then((r) => (r && r.ok ? r.json() : null))
        .then((j) => {
          if (j && typeof j.country === "string" && j.country.length === 2) {
            detectedCountry = j.country.toUpperCase();
            try { sessionStorage.setItem(GEO_CACHE_KEY, detectedCountry); } catch (e) {}
            maybeShowRegionBanner();
          }
        })
        .catch(() => { /* non-fatal */ });
    } catch (e) { /* non-fatal */ }
  }
})();
